import { useMemo, useEffect } from "react";
import {
  leadPageMainTabs,
  LeadPageTab,
} from "@/modules/lead-page/new/constants";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import usePassport from "@/modules/common/hooks/passport";
import useLeadTracks from "@/modules/lead-page/new/components/developer-tracks/hooks/use-lead-tracks.hook";
import { LeadType } from "@/modules/common/dtos/index.v2";

interface UseReviewsTabVisibilityParams {
  activeTab: LeadPageTab;
  setActiveTab: (tab: LeadPageTab) => void;
  leadUid?: string;
  leadType?: Partial<LeadType>;
}

export const useReviewsTabVisibility = ({
  activeTab,
  setActiveTab,
  leadUid,
  leadType,
}: UseReviewsTabVisibilityParams) => {
  const { permissions: currentUserPermissions } = usePassport();

  const isReviewsTabVisible = useMemo(() => {
    // Check permission first (synchronous, instant)
    const hasReviewsTabPermission = userHasPermission(
      {
        name: PermissionsEnum.CAN_VIEW_RATINGS_TAB,
        level: PermissionLevel.READ,
      },
      currentUserPermissions
    );

    // If user has permission, show tab immediately without waiting for API
    if (hasReviewsTabPermission) {
      return true;
    }

    // If no permission, check API error (only if API call is done)
    return false;
  }, [currentUserPermissions]);

  const { data: tracksData } = useLeadTracks({
    leadUId: leadUid || "",
    enabled: !!leadUid,
  });

  const hasDeveloperTracks = leadType?.has_developer_tracks ?? leadType?.hasDeveloperTracks ?? false;
  const hasExistingTracks = (tracksData?.results?.length ?? 0) > 0;

  const isDeveloperTracksTabVisible = useMemo(() => {
    const hasDeveloperTracksTabPermission = userHasPermission(
      {
        name: PermissionsEnum.DEVELOPER_TRACKS_TAB,
        level: PermissionLevel.READ,
      },
      currentUserPermissions
    );

    return hasDeveloperTracksTabPermission && (hasDeveloperTracks || hasExistingTracks);
  }, [currentUserPermissions, hasDeveloperTracks, hasExistingTracks]);

  // Filter tabs based on visibility rules
  const filteredTabs = useMemo(() => {
    return leadPageMainTabs.filter((tab) => {
      if (tab.id === LeadPageTab.REVIEWS && !isReviewsTabVisible) {
        return false;
      }
      if (tab.id === LeadPageTab.DEVELOPER_TRACKS && !isDeveloperTracksTabVisible) {
        return false;
      }
      return true;
    });
  }, [isReviewsTabVisible, isDeveloperTracksTabVisible]);

  // If Reviews tab is hidden and currently active, switch to Activity tab
  useEffect(() => {
    if (activeTab === LeadPageTab.REVIEWS && !isReviewsTabVisible) {
      setActiveTab(LeadPageTab.ACTIVITY);
    }
    if (activeTab === LeadPageTab.DEVELOPER_TRACKS && !isDeveloperTracksTabVisible) {
      setActiveTab(LeadPageTab.ACTIVITY);
    }
  }, [activeTab, isReviewsTabVisible, isDeveloperTracksTabVisible, setActiveTab]);

  return { isReviewsTabVisible, isDeveloperTracksTabVisible, filteredTabs };
};
